import React, { useState, useEffect } from 'react';
import { FiSearch, FiChevronLeft, FiChevronRight, FiUser, FiFileText, FiSettings, FiDatabase, FiBell } from 'react-icons/fi';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../contexts/ToastContext';
import apiClient, { endpoints } from '../api/client';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    admin_id: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalLogs: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20
  });
  const { showError } = useToast();

  useEffect(() => {
    fetchAuditLogs();
  }, [searchTerm, filters, pagination.currentPage]);
  
  const capitalize = (str) => {
    if (!str) return str; // guard against empty/null
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.admin_id) params.append('admin_id', filters.admin_id);

      const response = await apiClient.get(`${API_ENDPOINTS.DASHBOARD_AUDIT_LOGS}?${params}`);
      
      if (response.data.success) {
        const responseData = response.data.data || {};
        const logsData = responseData.logs || [];
        const paginationData = responseData.pagination || {};
        
        setLogs(Array.isArray(logsData) ? logsData : []);
        setPagination(prev => ({
          ...prev,
          currentPage: paginationData.current_page || paginationData.currentPage || 1,
          totalPages: paginationData.total_pages || paginationData.totalPages || 0,
          totalLogs: paginationData.total || paginationData.totalLogs || 0,
          hasNext: paginationData.has_next || paginationData.hasNext || false,
          hasPrev: paginationData.has_prev || paginationData.hasPrev || false
        }));
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setLogs([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalLogs: 0,
        hasNext: false,
        hasPrev: false,
        limit: 20
      });
      
      if (err.response?.status === 403) {
        setError('Access denied. Super admin privileges required.');
        showError('Access denied. Super admin privileges required.');
      } else if (err.response?.status === 404) {
        setError('Audit logs endpoint not found. Please check your API configuration.');
        showError('Audit logs endpoint not found.');
      } else {
        setError('Failed to load audit logs. Please try again later.');
        showError('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getActionBadge = (action) => {
    const actionClasses = {
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800',
      LOGIN: 'bg-purple-100 text-purple-800',
      LOGOUT: 'bg-gray-100 text-gray-800',
      VIEW: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        actionClasses[action] || 'bg-gray-100 text-gray-800'
      }`}>
        {action}
      </span>
    );
  };



  const getEntityBadge = (entityType) => {
    const entityClasses = {
      user: 'bg-blue-100 text-blue-800',
      certificate: 'bg-green-100 text-green-800',
      reminder: 'bg-yellow-100 text-yellow-800',
      admin: 'bg-purple-100 text-purple-800'
    };
    
    const iconMap = {
      user: FiUser,
      certificate: FiFileText,
      reminder: FiSettings,
      admin: FiUser
    };
    
    const Icon = iconMap[entityType?.toLowerCase()] || FiDatabase;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        entityClasses[entityType?.toLowerCase()] || 'bg-gray-100 text-gray-800'
      }`}>
        <Icon className="mr-1 h-3 w-3" />
        {entityType}
      </span>
    );
  };

  const formatLogDetails = (details) => {
    if (!details) return 'N/A';
    
    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    } catch {
      return details.toString();
    }
  };

  return (
    <div className="page-content">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="mt-1 text-sm text-gray-600">
          Track all administrative actions and system changes
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-5">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Audit Logs
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Review every action taken in the system for transparency and accountability. Keep operations compliant and secure.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search logs..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Action Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="VIEW">View</option>
            </select>

            {/* Entity Type Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            >
              <option value="">All Entities</option>
              <option value="user">User</option>
              <option value="certificate">Certificate</option>
              <option value="reminder">Reminder</option>
              <option value="admin">Admin</option>
            </select>

            {/* Admin Filter */}
            <input
              type="text"
              placeholder="Filter by admin ID..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.admin_id}
              onChange={(e) => handleFilterChange('admin_id', e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Audit Logs ({pagination.totalLogs})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!Array.isArray(logs) || logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        {error ? 'Error loading audit logs' : 'No audit logs found'}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {error ? 'Please check your permissions and try again.' : 
                         searchTerm || filters.action || filters.entity_type || filters.admin_id ? 
                         'Try adjusting your search or filters.' : 
                         'Audit logs will appear here as administrative actions are performed.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => {
                    // Safety check for log object
                    if (!log || typeof log !== 'object') {
                      return null;
                    }
                    
                    return (
                      <tr key={log.id || `log-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateTime(log.timestamp || log.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {log.admin.role.split("_").map((item) => capitalize(item)).join(" ") ?? 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {log.admin_id || log.adminId || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            {getEntityBadge(log.entity_type || log.entityType)}
                            {(log.entity_id || log.entityId) && (
                              <div className="text-xs text-gray-500">
                                ID: {log.entity_id || log.entityId}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={log.ip}>
                            {log.ip}
                          </div>
                        </td>
                      </tr>
                    );
                  }).filter(Boolean)
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;